/**
 * Main Audio Engine
 * 
 * Handles Sacred and Realtime audio processing
 * 
 * EXPANDED PURPOSE:
 * - Sacred frequency generation
 * - Realtime sensor sonification
 * - Harmonic synthesis
 * - Dynamic mixing
 * - Audio scene management
 */

export class MainAudioEngine {
  private context: AudioContext | null = null;
  private masterGain: GainNode | null = null;
  private isReady: boolean = false;

  constructor() {
    console.log('🎵 Main Audio Engine created');
  }

  /**
   * Initialize the engine
   */
  public async initialize(): Promise<void> {
    console.log('🎵 Initializing Main Audio Engine...');

    const AudioContextClass = window.AudioContext || (window as any).webkitAudioContext;
    this.context = new AudioContextClass();
    
    console.log(`   Context: ${this.context.sampleRate}Hz`);

    // Create master gain with safe default volume
    this.masterGain = this.context.createGain();
    const initialGain = Math.pow(10, -20 / 20); // -20dB default
    this.masterGain.gain.value = initialGain;
    
    // Connect master gain to destination (critical for audio output!)
    this.masterGain.connect(this.context.destination);
    
    console.log('   Master Volume: -20dB');
    console.log('   Connected to audio destination');

    this.isReady = true;
    console.log('✅ Main Audio Engine initialized');
  }

  /**
   * Get audio context
   */
  public getContext(): AudioContext | null {
    return this.context;
  }

  /**
   * Get master gain node
   */
  public getMasterGain(): GainNode | null {
    return this.masterGain;
  }

  /**
   * Set volume
   */
  public setVolume(volumeDb: number): void {
    if (!this.masterGain) {
      console.warn('⚠️ Cannot set volume: Engine not initialized');
      return;
    }

    const clampedDb = Math.max(-40, Math.min(0, volumeDb));
    const gain = Math.pow(10, clampedDb / 20);
    
    this.masterGain.gain.value = gain;
    console.log(`🔊 Main Engine Volume: ${clampedDb}dB`);
  }

  /**
   * Resume audio context
   */
  public async resume(): Promise<void> {
    if (this.context && this.context.state === 'suspended') {
      await this.context.resume();
      console.log('🎵 Main Audio Engine resumed');
    }
  }

  /**
   * Cleanup
   */
  public async cleanup(): Promise<void> {
    if (this.context && this.context.state !== 'closed') {
      await this.context.close();
      console.log('   Main Audio Engine closed');
    }

    this.context = null;
    this.masterGain = null;
    this.isReady = false;
  }

  /**
   * Check if ready
   */
  public ready(): boolean {
    return this.isReady;
  }
}

